import { API_BASE } from '@/shared/services/api';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CheckCircle2, Database, Download, HardDrive, Loader2, RefreshCw, RotateCcw,
  Server, Shield, X, XCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';

const POLL_MS = 1200;

const FLOW_STEPS = [
  { key: 'connect', label: 'Connect' },
  { key: 'server-backup', label: 'Backup' },
  { key: 'download', label: 'Download' },
  { key: 'local-safety-backup', label: 'Safety' },
  { key: 'local-restore', label: 'Restore' },
  { key: 'verify', label: 'Verify' },
];

const formatBytes = (value) => {
  const size = Number(value || 0);
  if (!Number.isFinite(size) || size <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const index = Math.min(Math.floor(Math.log(size) / Math.log(1024)), units.length - 1);
  return `${(size / (1024 ** index)).toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
};

const formatDate = (value) => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value || '-';
  return parsed.toLocaleString();
};

const statusLabel = (status) => {
  const value = String(status || '').toLowerCase();
  if (value === 'success' || value === 'ok') return 'OK';
  if (value === 'running') return 'Running';
  if (value === 'failed' || value === 'fail') return 'Failed';
  return value || 'Idle';
};

const stepTone = (step) => {
  const status = String(step?.status || '').toLowerCase();
  if (status === 'ok' || status === 'success') return 'ok';
  if (status === 'fail' || status === 'failed') return 'fail';
  if (status === 'running') return 'running';
  return 'idle';
};

const toneClasses = {
  ok: 'border-green-400 bg-green-50 text-green-800',
  fail: 'border-red-400 bg-red-50 text-red-800',
  running: 'border-emerald-400 bg-emerald-50 text-emerald-800',
  idle: 'border-slate-200 bg-white text-slate-500',
};

const request = async (path, options = {}) => {
  const token = localStorage.getItem('ezerpToken');
  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.detail || data.message || `HTTP ${response.status}`);
  }
  return data;
};

function DbBackupModal({ open, onClose }) {
  const [state, setState] = useState(null);
  const [activeRun, setActiveRun] = useState(null);
  const [selectedZip, setSelectedZip] = useState('');
  const [error, setError] = useState('');
  const busy = activeRun?.status === 'running';

  const latestServerBackup = useMemo(
    () => (state?.backups || []).find((item) => item.kind === 'server') || null,
    [state]
  );

  const stepMap = useMemo(
    () => Object.fromEntries((activeRun?.steps || []).map((step) => [step.key, step])),
    [activeRun]
  );

  const flowPercent = useMemo(() => {
    const completed = FLOW_STEPS.filter((item) => stepTone(stepMap[item.key]) === 'ok').length;
    const runningIndex = FLOW_STEPS.findIndex((item) => stepTone(stepMap[item.key]) === 'running');
    const effective = runningIndex >= 0 ? runningIndex + 0.45 : completed;
    return Math.min(100, Math.max(0, (effective / Math.max(1, FLOW_STEPS.length - 1)) * 100));
  }, [stepMap]);

  const loadState = useCallback(async () => {
    const payload = await request('/api/help-center/db-backup/state');
    setState(payload);
    if (payload.activeRun || payload.latestRun) setActiveRun(payload.activeRun || payload.latestRun);
    if (!selectedZip && payload.backups?.length) {
      const preferred = payload.backups.find((item) => item.kind === 'server') || payload.backups[0];
      setSelectedZip(preferred.path);
    }
  }, [selectedZip]);

  const loadRun = useCallback(async (runId) => {
    const payload = await request(`/api/help-center/db-backup/runs/${runId}`);
    setActiveRun(payload);
    if (payload.status !== 'running') {
      await loadState();
    }
  }, [loadState]);

  useEffect(() => {
    if (!open) return;
    setError('');
    loadState().catch((err) => setError(err.message));
  }, [open, loadState]);

  useEffect(() => {
    if (!open || !activeRun?.runId || activeRun.status !== 'running') return undefined;
    const timer = window.setInterval(() => {
      loadRun(activeRun.runId).catch((err) => setError(err.message));
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [open, activeRun?.runId, activeRun?.status, loadRun]);

  const startServerBackup = async () => {
    setError('');
    try {
      const run = await request('/api/help-center/db-backup/server-backup', { method: 'POST' });
      setActiveRun(run);
    } catch (err) {
      setError(err.message);
    }
  };

  const startLocalRestore = async () => {
    setError('');
    const zipToRestore = selectedZip || latestServerBackup?.path || '';
    if (!zipToRestore) {
      setError('Select a server backup ZIP before restore.');
      return;
    }
    const confirmed = window.confirm('This will create a local safety backup, then overwrite the local EzERP DB with the selected server backup. Continue?');
    if (!confirmed) return;
    try {
      const run = await request('/api/help-center/db-backup/local-restore', {
        method: 'POST',
        body: JSON.stringify({ backupZip: zipToRestore }),
      });
      setActiveRun(run);
      window.setTimeout(() => {
        loadRun(run.runId).catch((err) => setError(err.message));
      }, 450);
    } catch (err) {
      setError(err.message);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-950/65 backdrop-blur-sm" onClick={busy ? undefined : onClose} />
      <div className="relative z-10 w-full max-w-5xl max-h-[92vh] overflow-hidden rounded-lg border bg-background shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b p-5">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-red-700">Banco de Dados</div>
            <h2 className="mt-2 text-xl font-semibold">Backup Server DB</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Backup zipado do banco do servidor, download para DB-Backup e restore protegido no banco local.
            </p>
          </div>
          <button type="button" className="rounded-full border p-2 hover:bg-muted disabled:opacity-40" onClick={onClose} disabled={busy} aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="max-h-[calc(92vh-96px)] space-y-4 overflow-y-auto p-5">
          {error ? (
            <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800">{error}</div>
          ) : null}

          <div className="grid gap-3 rounded-lg border p-4 text-sm md:grid-cols-3">
            <div><strong>Servidor:</strong> {state?.serverUser || '-'}@{state?.serverHost || '-'}</div>
            <div><strong>Env:</strong> {state?.backendEnvPath || '-'}</div>
            <div><strong>Pasta local:</strong> {state?.backupFolder || '-'}</div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button type="button" onClick={startServerBackup} disabled={busy} className="gap-2">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Server className="h-4 w-4" />}
              Backup Server DB
            </Button>
            <Button type="button" variant="outline" onClick={startLocalRestore} disabled={busy || !(selectedZip || latestServerBackup)} className="gap-2">
              <RotateCcw className="h-4 w-4" />
              Unzip and overwrite local DB
            </Button>
            <Button type="button" variant="ghost" onClick={() => loadState().catch((err) => setError(err.message))} disabled={busy} className="gap-2">
              <RefreshCw className="h-4 w-4" />
              Refresh
            </Button>
          </div>

          <section className="rounded-lg border p-5">
            <div className="relative mx-auto max-w-4xl">
              <div className="absolute left-8 right-8 top-5 h-1 rounded bg-muted" />
              <div className="absolute left-8 top-5 h-1 rounded bg-emerald-400 transition-all" style={{ width: `calc((100% - 4rem) * ${flowPercent / 100})` }} />
              <div className="relative grid grid-cols-6 gap-2">
                {FLOW_STEPS.map((item) => {
                  const tone = stepTone(stepMap[item.key]);
                  return (
                    <div key={item.key} className="flex flex-col items-center gap-2 text-center">
                      <span className={`flex h-10 w-10 items-center justify-center rounded-full border-2 bg-background ${toneClasses[tone]}`}>
                        {tone === 'ok' ? <CheckCircle2 className="h-5 w-5" /> : tone === 'fail' ? <XCircle className="h-5 w-5" /> : tone === 'running' ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
                      </span>
                      <strong className="text-xs">{item.label}</strong>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Processo</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {(activeRun?.steps || []).length ? activeRun.steps.map((step) => {
                  const tone = stepTone(step);
                  return (
                    <div key={step.key} className={`rounded-md border p-3 text-sm ${toneClasses[tone]}`}>
                      <div className="flex items-center justify-between gap-2">
                        <strong>{step.label}</strong>
                        <Badge variant="outline">{statusLabel(step.status)}</Badge>
                      </div>
                      <div className="mt-1 break-words text-xs opacity-80">{step.detail || '-'}</div>
                    </div>
                  );
                }) : (
                  <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">No active run.</div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Backups em DB-Backup</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {(state?.backups || []).length ? state.backups.map((item) => (
                  <label key={item.path} className="flex cursor-pointer items-start gap-3 rounded-md border p-3 text-sm hover:bg-muted/40">
                    <input
                      type="radio"
                      name="db-backup-zip"
                      className="mt-1"
                      checked={selectedZip === item.path}
                      onChange={() => setSelectedZip(item.path)}
                    />
                    <span className="min-w-0">
                      <strong className="block truncate">{item.name}</strong>
                      <small className="text-muted-foreground">{item.kind} | {formatBytes(item.sizeBytes)} | {formatDate(item.modifiedAt)}</small>
                    </span>
                  </label>
                )) : (
                  <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">No ZIP found yet.</div>
                )}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Log</CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="min-h-[120px] overflow-x-auto rounded-lg bg-zinc-950 p-3 text-xs text-zinc-100">
                {(activeRun?.logs || []).join('\n') || 'Waiting for operation.'}
              </pre>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default function HelpCenterDbBackupPanel() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5 text-red-600" />
            Backup Server DB
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Backup zipado do banco do servidor, download para DB-Backup e restore protegido no banco local.
          </p>
          <div className="grid gap-3 md:grid-cols-3">
            <div className="rounded-lg border p-3 text-sm">
              <Server className="mb-2 h-4 w-4 text-muted-foreground" />
              <strong>Servidor</strong>
              <p className="text-muted-foreground">Origem viva do banco EzERP.</p>
            </div>
            <div className="rounded-lg border p-3 text-sm">
              <Download className="mb-2 h-4 w-4 text-muted-foreground" />
              <strong>DB-Backup</strong>
              <p className="text-muted-foreground">ZIP local versionado para restore.</p>
            </div>
            <div className="rounded-lg border p-3 text-sm">
              <Shield className="mb-2 h-4 w-4 text-muted-foreground" />
              <strong>Safety first</strong>
              <p className="text-muted-foreground">Backup local antes de sobrescrever.</p>
            </div>
          </div>
          <Button type="button" onClick={() => setOpen(true)} className="gap-2">
            <HardDrive className="h-4 w-4" />
            Backup Server DB
          </Button>
        </CardContent>
      </Card>
      <DbBackupModal open={open} onClose={() => setOpen(false)} />
    </>
  );
}
